/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import format from 'date-fns/format';

import { RMWContext } from 'app/shared/providers/RMWContext';

// Multibrand-UI Components
import Col from '@zg-rentals/multibrand-ui/lib/Col';
import Row from '@zg-rentals/multibrand-ui/lib/Row';
import StyledContainer from '@zg-rentals/multibrand-ui/lib/StyledContainer';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import Link from '@zg-rentals/multibrand-ui/lib/Link';
import Table from '@zg-rentals/multibrand-ui/lib/Table';
import HelpCircleFill from '@zg-rentals/multibrand-ui/lib/icons/HelpCircleFill';

import {
    Button,
    TextButton,
    Spacer,
} from '@zillow/constellation';

// TODO: update mui Row, Col to use zrs-comet
import { Container } from '@zillow/zrs-comet';

import { Link as v4Link } from 'react-router-dom';

// Constants
import { BILLING_PAGE } from 'app/shared/constants/RouteConstants';
import ModalConstants from 'app/shared/constants/ModalConstants';
import LoadingConstants from 'app/shared/constants/LoadingConstants';
import BillingConstants from 'app/shared/constants/BillingConstants';

// Actions
import ModalActions from 'app/shared/actions/ModalActions';
import BillingActions from 'app/shared/actions/BillingActions';

// Components
import PermissionCheck from 'app/shared/components/PermissionCheck';
import HeadWrapper from 'app/shared/components/HeadWrapper';
import TableMFH from 'app/shared/components/TableMFH';
import BillingError from 'app/shared/components/Billing/BillingError';
import BillingFilter from 'app/shared/components/Billing/BillingFilter';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';
import Breadcrumb from 'app/shared/components/Breadcrumb';
import { withErrorBoundary } from 'app/shared/components/ErrorBoundary';
import Tooltip from '@zg-rentals/multibrand-ui/lib/Tooltip';

// containers
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';

// Selectors
import {
    AppSelectors,
    RoutingSelectors,
    BillingSelectors,
    UserSelectors,
} from 'app/shared/selectors';

// Utils
import numberUtils from 'app/shared/utils/numberUtils';
import billingUtils from 'app/shared/utils/billingUtils';
import accessUtils from 'app/shared/utils/accessUtils';

const propTypes = {
    currentInvoice: PropTypes.object,
    currentInvoiceLoadingState: PropTypes.string,
    currentInvoiceErrorMessage: PropTypes.string,
};

const defaultProps = {
    currentInvoice: null,
    currentInvoiceLoadingState: LoadingConstants.INITIAL,
    currentInvoiceErrorMessage: null,
};

const DEFAULT_PAGE_TITLE = 'Invoice';
const { INVOICE_TYPES } = BillingConstants;

// Stylesheets
import './style.scss';

const formatDate = value => value && format(new Date(value), 'MM/DD/YYYY');

const InvoicePage = ({
    match,
    dispatch,
    currentInvoice,
    currentInvoiceLoadingState,
    currentInvoiceErrorMessage,
    isScreenMobile,
    currentInvoiceFilters,
    currentInvoiceLineItems,
    cookieEnabledServices,
    user,
}) => {
    const paymentsEnabled = accessUtils.canAccessInvoicePayments({ user, cookieEnabledServices });
    const { paths } = React.useContext(RMWContext);
    const invoiceColumnConfig = [
        {
            width: 4,
            header: 'Property',
            dataKey: 'propertyName',
        },
        {
            width: 2,
            header: 'Product',
            dataKey: 'chargeName',
            renderer: (data) => {
                if (data.chargeName.includes('Zillow Feed Connect')) {
                    return (
                        <div>
                            <Tooltip
                                trigger="hover"
                                type="primary"
                                targetClass="Tooltip-Tooltip-target"
                                className="Tooltip-Tooltip"
                            >
                                <Tooltip.Target>
                                    {data.chargeName}
                                    <HelpCircleFill size="sm" />
                                </Tooltip.Target>
                                <div className="HeaderTooltip-text">
                                    Billed per published unit per day.
                                </div>
                            </Tooltip>
                        </div>
                    );
                }
                return data.chargeName;
            },
        },
        {
            width: 1,
            header: currentInvoice && currentInvoice.invoiceType === INVOICE_TYPES.SINGLE_FAMILY_DAILY_LISTINGS
                ? 'Active Days'
                : 'QTY',
            dataKey: 'quantity',
            align: 'right',
            renderer: (value) => {
                const { quantity } = value;
                if (isNaN(quantity)) return '-';
                return quantity % 1 === 0 ? quantity : quantity.toFixed(2);
            },
        },
        {
            width: 2,
            header: currentInvoice && currentInvoice.invoiceType === INVOICE_TYPES.SINGLE_FAMILY_DAILY_LISTINGS
                ? 'Daily Rate'
                : 'Unit Price',
            dataKey: 'unitPrice',
            renderer: (value) => {
                if (!value || !value.quantity) return '-';
                return numberUtils.asCurrencyWithDefault(value.chargeAmount / value.quantity, '-');
            },
            align: 'right',
        },
        {
            width: 2,
            header: 'Amount',
            dataKey: 'chargeAmount',
            renderer: value => numberUtils.asCurrency(value.chargeAmount, '$0,0.00'),
            align: 'right',
        },
    ];

    const mobileColumnConfig = [
        {
            width: 4,
            header: 'Property',
            dataKey: 'propertyName',
        },
        {
            width: 4,
            header: 'Product',
            dataKey: 'chargeName',
        },
        {
            width: 3,
            header: 'Amount',
            dataKey: 'chargeAmount',
            renderer: value => numberUtils.asCurrency(value.chargeAmount, '$0,0.00'),
            align: 'right',
        },
    ];

    const columnConfig = isScreenMobile ? mobileColumnConfig : invoiceColumnConfig;

    const summaryColumnConfig = [
        {
            dataKey: 'amount',
            width: 4,
            header: 'Current Invoice Amount',
            renderer: value => numberUtils.asCurrency(value.amount, '$0,0.00'),
            align: 'right',
        },
        {
            dataKey: 'balance',
            width: 4,
            header: 'Current Invoice Balance',
            renderer: value => numberUtils.asCurrency(value.balance, '$0,0.00'),
            align: 'right',
        },
        {
            dataKey: 'totalAccountBalance',
            width: 4,
            header: 'Total Account Balance',
            renderer: value => numberUtils.asCurrencyWithDefault(value.totalAccountBalance, '-'),
            align: 'right',
        },
    ];

    const historyColumnConfig = [
        {
            dataKey: 'date',
            width: 3,
            header: 'Payment Date',
            renderer: value => formatDate(parseFloat(value.effectiveDate)),
            align: 'center',
        },
        {
            width: 3,
            header: 'Amount Paid',
            renderer: value => numberUtils.asCurrency(value.appliedPaymentAmount, '$0,0.00'),
            align: 'center',
        },
        {
            width: 3,
            header: 'Payment Number',
            dataKey: 'paymentNumber',
            align: 'center',
        },
        {
            width: 3,
            header: 'Transaction Status',
            dataKey: 'status',
            align: 'center',
            renderer: value => (value.status === 'processed' ? 'Complete' : value.status),
        },
    ];

    if (paymentsEnabled) {
        historyColumnConfig.splice(1, 0, {
            width: 3,
            header: 'Payment Method',
            align: 'center',
            renderer: billingUtils.formatPaymentMethod,
        });
        historyColumnConfig.push({
            width: 3,
            header: 'Paid By',
            align: 'center',
            renderer: value => (value.email ? value.email : '-'),
        });
    }

    const handleEditClick = (accountNumber) => {
        window.router.transitionTo(`${paths.billingPage}/account/${accountNumber}`);
    };

    React.useEffect(() => {
        dispatch(BillingActions.getInvoice(match.params.invoiceId));
        return () => {
            dispatch(BillingActions.clearCurrentInvoice());
        };
    }, []);

    const handlePayInfo = () => dispatch(ModalActions.showModal({
        modalType: ModalConstants.PAYMENT_INFO_MODAL,
        handleCloseModal: () => dispatch(ModalActions.hideModal()),
        useModalDialog: false,
    }));

    // TODO: hide this until we can actually map payment history
    const renderPaymentHistory = () => { // eslint-disable-line no-unused-vars
        if (currentInvoice.payments.length === 0) return null;
        return (
            <StyledContainer className="InvoicePage-card" borderFull={true}>
                <Row className="InvoicePage-card-heading" noPadding={true}>
                    <Col xs={12}>
                        <Text type="h3">
                            Payment History
                        </Text>
                    </Col>
                </Row>
                <TableMFH
                    columnConfig={historyColumnConfig}
                    data={currentInvoice.payments}
                />
            </StyledContainer>
        );
    };

    const renderInvoiceDetails = () => (
        <Container className="InvoicePage-container">
            <StyledContainer className="InvoicePage-card" borderFull={true}>
                <Row className="InvoicePage-card-heading" noPadding={true}>
                    <Col xs={12}>
                        <Text type="h3">
                            Account Summary
                        </Text>
                    </Col>
                </Row>
                <TableMFH
                    columnConfig={summaryColumnConfig}
                    data={[
                        currentInvoice,
                    ]}
                    isMobile={isScreenMobile}
                    idKey="invoice-summary-items"
                />
            </StyledContainer>
            {renderPaymentHistory()}
            {currentInvoiceLineItems.length > 0 && (
                <StyledContainer className="InvoicePage-card" borderFull={true}>
                    <Row className="InvoicePage-card-heading" noPadding={true}>
                        <Col xs={12}>
                            <Text type="h3">
                                Invoice Details
                            </Text>
                        </Col>
                    </Row>
                    <TableMFH
                        onSaveFilters={newFilters => dispatch(BillingActions.setInvoiceFilters(newFilters))}
                        columnConfig={columnConfig}
                        data={currentInvoiceLineItems}
                        FilterComponent={BillingFilter}
                        filters={currentInvoiceFilters}
                        isMobile={isScreenMobile}
                        idKey="invoice-line-items"
                    />
                </StyledContainer>
            )}
        </Container>
    );

    const handleDownloadClick = () => {
        const invoiceNumbers = [];
        invoiceNumbers.push(match.params.invoiceId);
        dispatch(BillingActions.downloadInvoices(invoiceNumbers));
    };

    const handleDownloadCsv = () => dispatch(BillingActions.downloadRocketshipCsv());

    const handlePayInvoiceClick = (invoiceId) => {
        window.router.transitionTo(`${paths.billingPage}/${paths.invoicePage}/${invoiceId}/payment`);
    };

    const renderHeader = () => (
        <React.Fragment>
            <Row>
                <Col xs={12}>
                    <Text type="body-small">
                        {billingUtils.formatZuoraRefresh(currentInvoice.lastZuoraSyncTime)}
                    </Text>
                    {paymentsEnabled ?
                        (
                            <Text type="body-small">
                                *Only credit card payment is supported for online payments right now.
                                For other types of payments, such as check, please follow the instructions
                                {' '}
                                <TextButton onClick={handlePayInfo}>here</TextButton>
                            </Text>
                        ) : (
                            <Text type="body-small">
                                {BillingConstants.BILLING_INFO_FOOTNOTE}
                            </Text>
                        )
                    }
                </Col>
            </Row>
            <Row noPadding={true}>
                <Col xs={12}>
                    <Text className="InvoicePage-heading" type="h3">
                        {currentInvoice.accountName}
                    </Text>
                    <Link
                        onClick={handleDownloadClick}
                        reactRouterLink={v4Link}
                        className="Utils-primary InvoicePage-download"
                    >
                        Download PDF
                    </Link>
                    {currentInvoice.invoiceType === INVOICE_TYPES.SINGLE_FAMILY_DAILY_LISTINGS
                        && currentInvoiceLineItems.length > 0
                        && (
                            <Link
                                onClick={handleDownloadCsv}
                                reactRouterLink={v4Link}
                                className="Utils-primary InvoicePage-download"
                            >
                                Download CSV
                            </Link>
                        )
                    }
                </Col>
            </Row>
            <Row noPadding={true}>
                <Col xs={12} md={6}>
                    <Table className="InvoicePage-details-table">
                        <Table.Row>
                            <Table.Col>INVOICE NUMBER</Table.Col>
                            <Table.Col>{match.params.invoiceId}</Table.Col>
                        </Table.Row>
                        <Table.Row>
                            <Table.Col>INVOICE DATE</Table.Col>
                            <Table.Col>{formatDate(currentInvoice.invoiceDate)}</Table.Col>
                        </Table.Row>
                        <Table.Row>
                            <Table.Col>SERVICE PERIOD</Table.Col>
                            <Table.Col>{billingUtils.calculateServicePeriod(currentInvoice.invoiceItems)}</Table.Col>
                        </Table.Row>
                    </Table>
                </Col>
                {currentInvoice.balance > 0 && !paymentsEnabled
                    && (
                        <Col className="InvoicePage-balance-details" xs={12} md={6}>
                            <Text>
                                {`Balance due by ${(new Date(currentInvoice.dueDate)).toLocaleDateString()}`}
                            </Text>
                            <Text className="InvoicePage-amount-due">
                                {numberUtils.asCurrency(currentInvoice.balance, '$0,0.00')}
                            </Text>
                            <div
                                onClick={handlePayInfo}
                                type="body"
                                className="Utils-primary Invoice-Page-how-to-pay"
                            >
                                How to pay?
                            </div>
                        </Col>
                    )}
                {currentInvoice.balance > 0 && paymentsEnabled
                    && (
                        <Col className="InvoicePage-balance-details" xs={12} md={6}>
                            <Text>
                                {currentInvoice.accountAutopay
                                    ? `Next payment date: ${billingUtils.getNextAutoPaymentDate()}`
                                    : `Balance due by ${(new Date(currentInvoice.dueDate)).toLocaleDateString()}`
                                }
                            </Text>
                            {currentInvoice.accountAutopay
                                ? (
                                    <Text>
                                        Auto Pay:
                                        {' '}
                                        <b>ON</b>
                                        {' '}
                                        <TextButton onClick={() => handleEditClick(currentInvoice.accountNumber)}>
                                            Edit
                                        </TextButton>
                                    </Text>
                                ) : (
                                    <Text className="InvoicePage-amount-due">
                                        {numberUtils.asCurrency(currentInvoice.balance, '$0,0.00')}
                                    </Text>
                                )
                            }
                            {!currentInvoice.accountAutopay && (
                                <React.Fragment>
                                    <Spacer marginTop="sm" />
                                    <Button
                                        buttonType="primary"

                                        onClick={() => handlePayInvoiceClick(currentInvoice.invoiceNumber)}
                                    >
                                        Make a payment
                                    </Button>
                                </React.Fragment>
                            )}
                        </Col>
                    )}
            </Row>
        </React.Fragment>
    );

    const renderLoadingHeader = () => (
        <React.Fragment>
            <Row noPadding={true}>
                <Col xs={8}>
                    <Text className="InvoicePage-heading" type="h3">
                        {match.params.invoiceId}
                    </Text>
                </Col>
            </Row>
            {currentInvoiceLoadingState !== LoadingConstants.ERROR && (
                <Row noPadding={true}>
                    <SpinnerContainer customClass="InvoicePage-spinner" />
                </Row>
            )}
        </React.Fragment>
    );

    const isLoaded = currentInvoiceLoadingState === LoadingConstants.SUCCESS;

    return (
        <AuthCheckWrapper requireStrongAuthentication={false}>
            <PermissionCheck route={BILLING_PAGE}>
                <div className="InvoicePage">
                    <HeadWrapper title={DEFAULT_PAGE_TITLE} />
                    <Container className="InvoicePage-container">
                        <Breadcrumb title="Back" redirectTo="/rental-manager/billing/due" />
                        <StyledContainer
                            className="InvoicePage-card InvoicePage-info-row"
                            borderFull={true}
                            borderRounded={true}
                        >
                            {isLoaded ? renderHeader() : renderLoadingHeader()}
                        </StyledContainer>
                    </Container>
                    {currentInvoiceErrorMessage && (
                        <Container>
                            <BillingError errorMsg={currentInvoiceErrorMessage} />
                        </Container>
                    )}
                    {isLoaded && renderInvoiceDetails()}
                </div>
            </PermissionCheck>
        </AuthCheckWrapper>
    );
};

const mapStateToProps = state => ({
    router: RoutingSelectors.makeRouting(state),
    currentInvoice: BillingSelectors.makeCurrentInvoiceSelector(state),
    currentInvoiceLineItems: BillingSelectors.makeCurrentInvoiceLineItems(state),
    currentInvoiceLoadingState: BillingSelectors.makeCurrentInvoiceLoadingState(state),
    currentInvoiceErrorMessage: BillingSelectors.makeCurrentInvoiceErrorMessage(state),
    currentInvoiceFilters: BillingSelectors.makeCurrentInvoiceFilters(state),
    downloadState: BillingSelectors.makeInvoicesDownloadState(state),
    downloadErrorMessage: BillingSelectors.makeInvoicesDownloadErrorMessage(state),
    isScreenMobile: AppSelectors.makeIsScreenMobile(state),
    cookieEnabledServices: AppSelectors.makeCookieEnabledServices(state),
    user: UserSelectors.makeUser(state),
});

InvoicePage.propTypes = propTypes;
InvoicePage.defaultProps = defaultProps;

export { InvoicePage };
export default withErrorBoundary(connect(mapStateToProps)(InvoicePage));
